/* eslint-disable camelcase */
import axios from 'axios';

import { getResource } from '../MultiLocationSaleModal/utils';

type PaymentLinkAmountLimits = {
  min: number;
  max: number;
};

function getPaymentLinkAmountLimits(): PaymentLinkAmountLimits {
  const min = 0.5;
  const max = 100000;

  return {
    min,
    max,
  };
}

export { getPaymentLinkAmountLimits };

/**
 *
 * @param amountParams - option and balanceCheckUrl
 * @param amountParams.balanceCheckUrl - Example: '/receipt.php?form_name=load_credit_account_statement&credit_account_id=${creditAccountId}&start=${startDate}&end=${endDate}'
 */
export const getDueAmountOrBalanceInCents = async ({
  option = 'due_amount',
  balanceCheckUrl,
}: {
  option?: 'due_amount' | 'balance';
  balanceCheckUrl: string;
}) => {
  const response = await fetch(balanceCheckUrl);
  if (!response.ok) {
    return undefined;
  }

  /**
   * There is no endpoint to get balance owed by a credit account for a selected date range so
   * Using /receipt.php?form_name=load_credit_account_statement to get the balance owed for the selected date range
   */
  const responseData = await response.text();
  const parsedHtml = new DOMParser().parseFromString(responseData, 'text/html');
  const selector =
    option === 'due_amount' ? '#credit-statement-due-amount' : '#credit-statement-balance';
  const owedAmount = parsedHtml.querySelector(selector)?.textContent;

  if (owedAmount !== null && owedAmount !== undefined) {
    return Math.ceil(parseFloat(owedAmount) * 100).toString(); // convert to cents
  }

  return undefined;
};

export const getPaymentLink = async ({
  amount,
  language,
  creditAccountId,
  shopId,
  registerId,
  employeeId,
}: {
  amount: string;
  language: string;
  shopId: string;
  employeeId: string;
  registerId: string;
  creditAccountId: string;
}): Promise<undefined | string> => {
  /**
   * Expected response from /admin/payment-link
    {
      data: {
        payment_link_id : string,
        "url" : payment link url
      },
      errors: [{
        //optional
      }]
    }
   */
  const [responseData, _error] = await getResource({
    requestFunction: () =>
      axios.post('/admin/payment-link', {
        credit_account_id: creditAccountId,
        shop_id: shopId,
        register_id: registerId,
        employee_id: employeeId,
        language,
        amount,
      }),
  });

  if (!responseData) {
    return undefined;
  }
  return responseData.data.url;
};

export const printCreditStatement = async ({
  hasLightSpeedPayments,
  includePaymentLink,
  balanceCheckUrl,
  creditAccountId,
  employeeId,
  language,
  registerId,
  shopId,
  dueAmountInCents,
  dueDate,
}: {
  hasLightSpeedPayments: boolean;
  includePaymentLink: boolean;
  language: string;
  shopId: string;
  employeeId: string;
  registerId: string;
  creditAccountId: string;
  balanceCheckUrl: string;
  dueAmountInCents: number;
  dueDate: string;
}) => {
  window.showNotifier('working');

  let statementPrintUrl = `${balanceCheckUrl}&language=${language}&print=1`;

  const { belowMinAmount, overMaxAmount } = hasCrossedPaymentLinkMaxMinAmount(dueAmountInCents);

  let shouldGeneratePaymentLink = false;

  if (
    hasLightSpeedPayments &&
    includePaymentLink &&
    dueAmountInCents !== 0 &&
    !belowMinAmount &&
    !overMaxAmount
  ) {
    shouldGeneratePaymentLink = true;
  }

  // If shop has LSP and amount is not more than PAY_LINK_AMOUNT_LIMIT, fetch payment link
  if (shouldGeneratePaymentLink) {
    const paymentLink: string | undefined = await getPaymentLink({
      amount: dueAmountInCents.toString(),
      language,
      shopId,
      registerId,
      employeeId,
      creditAccountId,
    });

    if (registerId === '' || registerId === '0') {
      window.showNotifier('warning', 'Unable to print statement. Select a register and try again.');
      window.hideNotifier('working');
      return;
    }

    if (paymentLink === undefined) {
      // When an API fails, paymentLink is undefined
      window.showNotifier('warning', 'Unable to generate payment link. Try again.');
    }
    if (paymentLink) {
      statementPrintUrl = `${statementPrintUrl}&payment_link=${paymentLink}`;
    }
  }

  window.hideNotifier('working');
  window.merchantos.utils.openUrl(
    `${statementPrintUrl}&due_amount=${dueAmountInCents / 100}&due_date=${dueDate}`,
    '_blank'
  );
};

export const emailCreditStatement = async (
  customer_id: string,
  email: string,
  statement_start_date: string,
  statement_end_date: string,
  language: string,
  payment_url: string | undefined,
  payment_due_date: string | undefined,
  amount: string
) => {
  const { status } = await fetch('/admin/email/credit-account-statement', {
    method: 'POST',
    body: JSON.stringify({
      customer_id,
      email,
      statement_start_date,
      statement_end_date,
      language,
      ...(payment_url && { payment_url }),
      ...(payment_due_date && { payment_due_date }),
      ...(amount && { amount }),
    }),
  });

  return status === 200;
};

/**
 *
 * @param amount the amount in cents
 * @returns
 */
export const hasCrossedPaymentLinkMaxMinAmount = (amount: number) => {
  return {
    overMaxAmount: amount > getPaymentLinkAmountLimits().max * 100,
    belowMinAmount: amount < getPaymentLinkAmountLimits().min * 100,
  };
};

export const updateAccountEmailRequest = async (
  accountId: string,
  customerId: string,
  inputValue: string
) => {
  try {
    const response = await fetch(`/API/V3/Account/${accountId}/Customer/${customerId}.json`, {
      body: JSON.stringify({
        Contact: {
          Emails: {
            ContactEmail: [
              {
                address: inputValue,
                useType: 'Primary',
              },
            ],
          },
        },
      }),
      headers: {
        'Content-Type': 'text/json',
      },
      method: 'PUT',
    });

    if (await response.json()) {
      return response.ok;
    }
    return true;
  } catch (err) {
    return false;
  }
};

export const fetchPrimaryCustomerEmail = async (customerId: string) => {
  let primaryEmail = '';

  try {
    const response = await fetch(
      `/API/V3/Account/${window.merchantos.account.id}/Customer/${customerId}.json?load_relations=["Contact"]`
    );

    if (response.status === 200) {
      const responseData = await response.json();

      const {
        Customer: {
          Contact: { Emails },
        },
      } = responseData;

      if (!Emails || !Emails.ContactEmail) {
        // will return an empty string if neither email1 or email2 are filled out in customer profile
        return '';
      }

      const customerCurrentEmailsArray: ContactEmail[] = Array.isArray(Emails.ContactEmail)
        ? Emails.ContactEmail
        : [Emails.ContactEmail];

      primaryEmail =
        customerCurrentEmailsArray.find((email) => (email as ContactEmail).useType === 'Primary')
          ?.address ?? '';
    }
  } catch (err) {
    // no error handling required here, if there is an error fetching emails for the customer, we will simply populate the input as an empty string
  }

  return primaryEmail;
};

export const fetchCustomerId = (): string | undefined => {
  const el = document.getElementById('customerInfo')?.dataset.customerId;

  return el ?? undefined;
};
